/**
 * src/components/Financial/modules/LatitudeTransitModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一・フチ取り削除】
 */
import React from "react";
import { Compass } from "lucide-react";
import * as Astro from "../../../astronomy.js";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";

/**
 * 緯度通過（Latitude Transits）計算ロジック
 */
export const calculateLatitudeTransits = function (
  timeline,
  selectedPlanets,
  targetLatitudes,
  isHelio,
) {
  var hits = [];
  if (
    !timeline ||
    timeline.length < 3 ||
    !selectedPlanets ||
    selectedPlanets.size === 0
  )
    return hits;

  var getDate = function (row) {
    return row.timestamp ? new Date(row.timestamp) : new Date(row.Date);
  };

  // 緯度取得ヘルパー
  var getLatAt = function (planet, date) {
    try {
      var time = Astro.MakeTime(date);
      var body = Astro.PLANET_BODIES[planet];
      if (planet === "Sun") return 0; // 太陽は常に緯度0

      var vector = isHelio
        ? Astro.HelioVector(body, time)
        : Astro.GeoVector(body, time, true);
      var r = Math.sqrt(
        vector.x * vector.x + vector.y * vector.y + vector.z * vector.z,
      );
      var lat = Math.asin(vector.z / r) * (180 / Math.PI);
      return lat;
    } catch (e) {
      return null;
    }
  };

  selectedPlanets.forEach(function (p) {
    for (var i = 1; i < timeline.length - 1; i++) {
      var d0 = getDate(timeline[i - 1]);
      var d1 = getDate(timeline[i]);
      var d2 = getDate(timeline[i + 1]);

      var v0 = getLatAt(p, d0);
      var v1 = getLatAt(p, d1);
      var v2 = getLatAt(p, d2);

      if (v0 === null || v1 === null || v2 === null) continue;

      // 1. 指定された緯度(0, 1, -1など)の通過判定
      targetLatitudes.forEach(function (target) {
        if ((v0 <= target && v1 > target) || (v0 >= target && v1 < target)) {
          hits.push({
            index: i,
            planet: p,
            type: "Cross",
            value: target,
            label: target + "°",
            color: PLANET_COLORS[p] || "#64748b",
          });
        }
      });

      // 2. 極値（ピーク）の判定
      var slope1 = v1 - v0;
      var slope2 = v2 - v1;
      if (slope1 > 0 && slope2 < 0) {
        hits.push({
          index: i,
          planet: p,
          type: "Peak",
          value: v1,
          label: "Max",
          color: PLANET_COLORS[p] || "#64748b",
        });
      } else if (slope1 < 0 && slope2 > 0) {
        hits.push({
          index: i,
          planet: p,
          type: "Valley",
          value: v1,
          label: "Min",
          color: PLANET_COLORS[p] || "#64748b",
        });
      }
    }
  });
  return hits;
};

/**
 * 描画レイヤー (ダークモード対応・フチ取り削除)
 */
export const LatitudeTransitLayer = function (props) {
  var { hits, displayRange, coords, planetColors, isDarkMode } = props;
  if (!hits) return null;

  return (
    <g className="latitude-transit-layer">
      {hits.map(function (hit, i) {
        if (
          hit.index < displayRange.start ||
          hit.index >= displayRange.start + displayRange.count
        )
          return null;
        var xPos = coords.getX(hit.index - displayRange.start);
        var pColor = planetColors[hit.planet] || hit.color;

        return (
          <g key={"lat-" + i}>
            <line
              x1={xPos}
              y1={coords.pad.t}
              x2={xPos}
              y2={coords.pad.t + coords.iH}
              stroke={pColor}
              strokeDasharray="1,2"
              opacity="0.5"
            />
            <g
              transform={"translate(" + xPos + ", " + (coords.pad.t - 55) + ")"}
            >
              {/* フチ取り(stroke)を削除 */}
              <text
                textAnchor="middle"
                fontSize="14"
                fill={pColor}
                fontWeight="bold"
              >
                {PLANET_SYMBOLS[hit.planet]}
              </text>
              <text
                y="12"
                textAnchor="middle"
                fontSize="9"
                fill={pColor}
                fontWeight="bold"
              >
                {hit.label}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 設定UI (ダークモード対応・デザイン統一)
 */
export const LatitudeTransitSettingsUI = function (props) {
  var { show, setShow, selectedPlanets, setSelectedPlanets, isDarkMode } =
    props;

  var planets = [
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
  };

  var togglePlanet = function (p) {
    var next = new Set(selectedPlanets);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setSelectedPlanets(next);
  };

  /**
   * ボタン共通スタイル (f98.jpg 基準)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  /**
   * 天体選択ボタンの共通スタイル
   */
  var plBtnSt = function (p, active) {
    var pColor = PLANET_COLORS[p] || "#6366f1";
    return {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 14px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      background: active
        ? isDarkMode
          ? "#2c2d96"
          : "#ffffff"
        : isDarkMode
          ? "#1c1f26"
          : "#f1f5f9",
      border:
        "1.5px solid " + (active ? pColor : isDarkMode ? "#2d313d" : "#e2e8f0"),
      color: active
        ? isDarkMode
          ? "#ffffff"
          : pColor
        : isDarkMode
          ? "#9ca3af"
          : "#64748b",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: theme.text,
          }}
        >
          <Compass size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />{" "}
          <span>緯度通過 (Latitude Transits)</span>
        </div>
        <button
          onClick={function () {
            setShow(!show);
          }}
          style={btnSt(show)}
        >
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
          transition: "opacity 0.2s ease-in-out",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {planets.map(function (p) {
            var isSelected = selectedPlanets.has(p);
            return (
              <button
                key={p}
                onClick={function () {
                  togglePlanet(p);
                }}
                style={plBtnSt(p, isSelected)}
              >
                <span style={{ fontSize: "14px" }}>{PLANET_SYMBOLS[p]}</span>{" "}
                {p}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: "12px", color: theme.subText }}>
          ※
          惑星が緯度0°（黄道面）を通過するノードポイントと、北緯・南緯のピークを検出します。
        </div>
      </div>
    </div>
  );
};
